import type { Currency } from "@redotech/money/currencies";
import { RedoClient } from "@redotech/redo-api-client";
import type { Stripe } from "stripe";

/**
 * Pull the server's `{ error }` payload out of a failed billing request. These
 * endpoints return merchant-actionable text — a card decline, an unsupported
 * instrument — so replacing it with our own generic string hides the reason the
 * merchant needs in order to fix it.
 */
export function billingErrorMessage(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return null;
  }
  const { response } = error;
  if (
    typeof response !== "object" ||
    response === null ||
    !("data" in response)
  ) {
    return null;
  }
  const { data } = response;
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return null;
  }
  const { error: message } = data;
  return typeof message === "string" && message.trim() ? message.trim() : null;
}

/**
 * POST /stripe
 */
export async function updateStripe(
  client: RedoClient,
  {
    paymentMethodID,
    primary,
    outboundLabels,
    setupIntentClientSecret,
    currency,
  }: {
    paymentMethodID: string;
    primary: boolean;
    outboundLabels?: { delete?: boolean };
    setupIntentClientSecret?: string | null;
    currency?: Currency;
  },
) {
  const response = await client.client.post(
    "stripe",
    {
      payment_method_id: paymentMethodID,
      primary,
      outbound_labels: outboundLabels,
      setup_intent_client_secret: setupIntentClientSecret ?? null,
      currency,
    },
    { headers: client.authorization() },
  );
  return response.data;
}

/**
 * POST /stripe/clear-bank-verification
 */
export async function clearBankVerification(
  client: RedoClient,
  { primary }: { primary: boolean },
) {
  const response = await client.client.post(
    "stripe/clear-bank-verification",
    { primary },
    { headers: client.authorization() },
  );
  return response.data;
}

/**
 * POST /stripe/setup-intent
 */
export async function createSetupIntent(
  client: RedoClient,
  { currency }: { currency?: Currency } = {},
) {
  const response = await client.client.post(
    "stripe/setup-intent",
    { currency },
    { headers: client.authorization() },
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return response.data as Stripe.SetupIntent;
}

/**
 * POST /stripe/retry
 * @param client
 */
export async function retryBilling(client: RedoClient, { id }: { id: string }) {
  const response = await client.client.post(
    "stripe/retry",
    { id },
    { headers: client.authorization() },
  );
  return response.data;
}

/**
 * POST /stripe/recovery-charge
 */
export async function chargeRecovery(
  client: RedoClient,
  { id, amount }: { id: string; amount: number },
) {
  const response = await client.client.post(
    "stripe/recovery-charge",
    { id, amount },
    { headers: client.authorization() },
  );
  return response.data;
}

/**
 * POST /stripe/refund-recovery-charge
 */
export async function refundRecoveryCharge(
  client: RedoClient,
  { id, amount }: { id: string; amount: number | null },
) {
  const response = await client.client.post(
    "stripe/refund-recovery-charge",
    { id, amount },
    { headers: client.authorization() },
  );
  return response.data;
}
